import { makeAutoObservable } from 'mobx';
import { Apportionment } from '../models/apportionments/Apportionment';
import {
  ApportionmentApi,
  ApportionmentApiResponse,
} from '../../api/ApportionmentApi';
import { ApportionmentType } from '../../config/Constants';

export class ApportionmentsStore {
  apportionments: Map<number, Apportionment> = new Map();
  entityApportionments: Map<number, number[]> = new Map();
  groupApportionments: Map<number, number[]> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  save = async (
    groupId: number,
    apportionment: Apportionment
  ): Promise<Apportionment> => {
    let appt;

    if (apportionment.id > 0) {
      appt = await ApportionmentApi.updateApportionment(
        groupId,
        apportionment.id,
        apportionment.toApi()
      );
    } else {
      appt = await ApportionmentApi.createApportionment(
        groupId,
        apportionment.toApi()
      );
    }

    return this.handleApportionmentLoad(groupId, appt, apportionment.id);
  };

  handleApportionmentLoad = (
    groupId: number,
    apiAppt: ApportionmentApiResponse,
    oldApptId: number = -1
  ) => {
    const appt = Apportionment.fromApi(apiAppt, groupId);

    if (oldApptId !== -1) {
      this.apportionments.delete(oldApptId);
    }
    this.apportionments.set(appt.id, appt);

    if (appt.type === ApportionmentType.CROSS_ENTITY) {
      this.groupApportionments.set(
        appt.groupId,
        this.replace(
          this.groupApportionments.get(appt.groupId),
          oldApptId,
          appt.id
        )
      );
    } else {
      this.entityApportionments.set(
        appt.entityId,
        this.replace(
          this.entityApportionments.get(appt.entityId),
          oldApptId,
          appt.id
        )
      );
    }

    return appt;
  };

  replace = (list: number[], oldId: number, newId: number): number[] => {
    if (!list) return [newId];
    if (list.includes(newId)) return list;

    const idx = list.indexOf(oldId);

    if (idx !== -1) {
      list[idx] = newId;
    } else {
      list.push(newId);
    }

    return list;
  };

  loadGroupApportionments = (groupId: number): Promise<Apportionment[]> => {
    return ApportionmentApi.getByGroup(groupId).then(
      (response: ApportionmentApiResponse[]) => {
        return response.map(apiAppt => {
          return this.handleApportionmentLoad(groupId, apiAppt);
        });
      }
    );
  };

  getApportionment = (id: number): Apportionment => {
    return this.apportionments.get(id);
  };

  deleteApportionment = async (apportionment: Apportionment) => {
    await ApportionmentApi.deleteApportionment(
      apportionment.groupId,
      apportionment.id
    );

    if (apportionment.type === ApportionmentType.CROSS_ENTITY) {
      this.groupApportionments.set(
        apportionment.groupId,
        this.groupApportionments
          .get(apportionment.groupId)
          .filter(id => id !== apportionment.id)
      );
    } else {
      this.entityApportionments.set(
        apportionment.entityId,
        this.entityApportionments
          .get(apportionment.entityId)
          .filter(id => id !== apportionment.id)
      );
    }

    this.apportionments.delete(apportionment.id);
  };

  getByGroupId = (groupId: number): Apportionment[] => {
    return (this.groupApportionments.get(groupId) || [])
      .map(apptId => this.getApportionment(apptId))
      .filter(appt => !!appt);
  };

  getByEntityId = (entityId: number): Apportionment[] => {
    return (this.entityApportionments.get(entityId) || [])
      .map(apptId => this.getApportionment(apptId))
      .filter(appt => !!appt);
  };
}
